import { useEffect, useState } from "react";
import { Pencil, Plus, FileDown, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { BrandDialog } from "@/pages/admin/BrandDialog";

export const BrandsTab = ({ onChange }) => {
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/brands").then((r) => setBrands(r.data));
  useEffect(() => { load(); }, []);

  const toggleArchive = async (b) => {
    const archived = !b.archived;
    if (archived && !window.confirm(`Archive "${b.name}"? Its ${b.product_count} SKUs will be hidden from the catalog but nothing is deleted.`)) return;
    await api.patch(`/admin/brands/${b.slug}/archive`, { archived });
    toast.success(archived ? `${b.name} archived — hidden from catalog` : `${b.name} restored to catalog`);
    load(); onChange?.();
  };

  const live = brands.filter((b) => !b.archived);
  const archived = brands.filter((b) => b.archived);

  const Row = ({ b }) => (
    <div className={`sr-card overflow-hidden flex ${b.archived ? "opacity-70" : ""}`} data-testid={`admin-brand-row-${b.slug}`}>
      <img src={b.image} alt="" className={`w-32 object-cover shrink-0 bg-slate-100 ${b.archived ? "grayscale" : ""}`} />
      <div className="p-4 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: b.accent }}>{b.category} · {b.product_count} SKUs · order {b.order}</p>
          {b.archived && <span className="text-[10px] uppercase tracking-wider font-bold rounded-full bg-slate-200 text-slate-600 px-2 py-0.5" data-testid={`admin-brand-archived-badge-${b.slug}`}>Archived</span>}
        </div>
        <h3 className="font-bold text-[#0B1F4B] mt-0.5">{b.name}</h3>
        <p className="text-xs text-slate-500 truncate">{b.tagline}</p>
        <p className="text-xs text-slate-400 mt-1">{b.highlights?.length || 0} highlights · {b.why_it_sells?.length || 0} why-it-sells · {b.ideal_buyers?.length || 0} buyers</p>
        <div className="mt-3 flex gap-2 flex-wrap items-center">
          <button onClick={() => setEditing(b)} className="sr-btn-outline !py-1.5 !px-3 text-xs" data-testid={`admin-brand-edit-${b.slug}`}><Pencil size={13} /> Edit</button>
          <button onClick={() => toggleArchive(b)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${b.archived ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-slate-300 text-slate-600 hover:border-[#E63946] hover:text-[#E63946]"}`} data-testid={`admin-brand-archive-toggle-${b.slug}`}>
            {b.archived ? <><ArchiveRestore size={13} /> Restore</> : <><Archive size={13} /> Archive</>}
          </button>
          {!b.archived && <a href={`${API}/brands/${b.slug}/sell-sheet.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E7CF2] px-2" data-testid={`admin-brand-sellsheet-${b.slug}`}><FileDown size={13} /> Sell sheet</a>}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-brands-tab">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Edit brand stories, highlights, why-it-sells bullets and ideal-buyer lists. Archive a brand to hide its whole line from the catalog without deleting data.</p>
        <div className="flex gap-2">
          <a href={`${API}/catalog/sell-sheet.pdf`} target="_blank" rel="noreferrer" className="sr-btn-outline !py-2" data-testid="admin-catalog-deck-download"><FileDown size={16} /> Full catalog PDF</a>
          <button onClick={() => setEditing({})} className="sr-btn-blue !py-2" data-testid="admin-brand-add-button"><Plus size={16} /> Add brand</button>
        </div>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">{live.map((b) => <Row key={b.slug} b={b} />)}</div>
      {archived.length > 0 && (
        <div className="mt-8" data-testid="admin-archived-brands">
          <p className="sr-label mb-3">Archived brands ({archived.length})</p>
          <div className="grid gap-4 md:grid-cols-2">{archived.map((b) => <Row key={b.slug} b={b} />)}</div>
        </div>
      )}
      {editing !== null && <BrandDialog brand={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />}
    </div>
  );
};
